import React, { useEffect, useState } from 'react';
import {
  Search, RefreshCw, Calendar, MessageSquare, Bot, ChevronLeft,
  ChevronRight, Clock, FileText, Download,
} from 'lucide-react';
import dayjs from 'dayjs';
import { eventsApi, sessionsApi, chatApi, adminApi } from '../../services/api';
import { ApiResponse, Event, Session, AppUser } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';

// ── Helpers ────────────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  'bg-blue-100 text-blue-700',
  'bg-purple-100 text-purple-700',
  'bg-green-100 text-green-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-teal-100 text-teal-700',
];

function avatarColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

const PAGE_SIZE = 10;

const defaultStartDateTime = (): string => dayjs().subtract(24, 'hour').format('YYYY-MM-DDTHH:mm');
const defaultEndDateTime = (): string => dayjs().format('YYYY-MM-DDTHH:mm');

// ── Conversation Message ───────────────────────────────────────────────────────

interface ConvMessage {
  userMessage: string;
  botResponse: string;
  timestamp: string;
  messageId?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

const Conversations: React.FC = () => {
  const { isAdmin, user } = useAuth();

  // Event / client selection
  const [events,           setEvents]           = useState<Event[]>([]);
  const [eventsLoading,    setEventsLoading]    = useState(false);
  const [selectedEventId,  setSelectedEventId]  = useState('');
  const [clients,          setClients]          = useState<AppUser[]>([]);
  const [loadingClients,   setLoadingClients]   = useState(false);
  const [selectedClientId, setSelectedClientId] = useState('');

  // Sessions
  const [sessions,         setSessions]         = useState<Session[]>([]);
  const [sessionsLoading,  setSessionsLoading]  = useState(false);
  const [sessionsPage,     setSessionsPage]     = useState(1);
  const [sessionsTotal,    setSessionsTotal]    = useState(0);
  const [searchInput,      setSearchInput]      = useState('');
  const [nameSearch,       setNameSearch]       = useState('');
  const [startDate,        setStartDate]        = useState(() => defaultStartDateTime());
  const [endDate,          setEndDate]          = useState(() => defaultEndDateTime());

  // Selected session & chat
  const [selectedSession,  setSelectedSession]  = useState<Session | null>(null);
  const [conversations,    setConversations]    = useState<ConvMessage[]>([]);
  const [historyLoading,   setHistoryLoading]   = useState(false);
  const [activeView,       setActiveView]       = useState<'chat' | 'summary'>('chat');

  // Summary
  const [summary,          setSummary]          = useState('');
  const [summaryLoading,   setSummaryLoading]   = useState(false);
  const [summaryError,     setSummaryError]     = useState('');

  // Export modal
  const [exportOpen,       setExportOpen]       = useState(false);
  const [exportLoading,    setExportLoading]    = useState(false);
  const [exportStart,      setExportStart]      = useState('');
  const [exportEnd,        setExportEnd]        = useState('');
  const [exportFormat,     setExportFormat]     = useState<'csv' | 'json' | 'both'>('both');
  const [exportError,      setExportError]      = useState('');

  // ── Fetch helpers ────────────────────────────────────────────────────────────

  const fetchClients = async () => {
    setLoadingClients(true);
    try {
      const resp = await adminApi.getAllUsers({ role: 'client', limit: 1000, isActive: true });
      setClients((resp.data as any)?.data?.users || []);
    } catch { /* ignore */ } finally { setLoadingClients(false); }
  };

  const fetchEvents = async (clientId?: string) => {
    setEventsLoading(true);
    try {
      const params: any = { page: 1, limit: 100 };
      if (isAdmin && clientId) params.clientId = clientId;
      else if (!isAdmin && user?._id) params.clientId = user._id;
      const res = await eventsApi.getAll(params);
      const list: Event[] = (res.data as ApiResponse<Event[]>).data || [];
      setEvents(list);
      if (!isAdmin && list.length > 0) setSelectedEventId(list[0]._id);
    } catch { /* ignore */ } finally { setEventsLoading(false); }
  };

  const fetchSessions = async (
    eventId: string,
    page: number,
    filters?: { search?: string; startDate?: string; endDate?: string },
  ) => {
    setSessionsLoading(true);
    try {
      const effectiveSearch = (filters?.search ?? nameSearch).trim();
      const effectiveStart = filters?.startDate ?? startDate;
      const effectiveEnd = filters?.endDate ?? endDate;

      const params: any = {
        page,
        limit: PAGE_SIZE,
        includeInactive: true,
        includeExpired: true,
      };
      if (effectiveSearch) params.search = effectiveSearch;

      if (effectiveStart) {
        const parsedStart = new Date(effectiveStart);
        if (Number.isNaN(parsedStart.getTime())) {
          toast.error('Please select a valid start date/time');
          return;
        }
        params.startDate = parsedStart.toISOString();
      }
      if (effectiveEnd) {
        const parsedEnd = new Date(effectiveEnd);
        if (Number.isNaN(parsedEnd.getTime())) {
          toast.error('Please select a valid end date/time');
          return;
        }
        params.endDate = parsedEnd.toISOString();
      }

      if (params.startDate && params.endDate && new Date(params.startDate) > new Date(params.endDate)) {
        toast.error('From date cannot be after To date');
        return;
      }

      const res = await sessionsApi.getByEvent(eventId, params);
      const apiRes = res.data as ApiResponse<Session[]>;
      setSessions(apiRes.data || []);
      setSessionsTotal((apiRes as any).pagination?.total || (apiRes.data?.length ?? 0));
    } catch {
      setSessions([]);
      setSessionsTotal(0);
    } finally { setSessionsLoading(false); }
  };

  const fetchHistory = async (sessionId: string) => {
    setHistoryLoading(true);
    setConversations([]);
    setSummary('');
    setSummaryError('');
    try {
      const res = await chatApi.getHistory(sessionId);
      const historyData = (res.data as any)?.data || {};
      const convos: ConvMessage[] = historyData.conversations || [];
      setConversations(convos);
      setSummary(historyData.sessionInfo?.summary || '');
    } catch { setConversations([]); } finally { setHistoryLoading(false); }
  };

  const generateSummary = async (regenerate: boolean = false) => {
    if (!selectedSession) return;
    setSummaryLoading(true);
    setSummaryError('');
    try {
      const res = await chatApi.getSummary(selectedSession.sessionId, regenerate ? { regenerate: true } : undefined);
      setSummary((res.data as any)?.data?.summary || '');
    } catch (err: any) {
      setSummaryError(err.response?.data?.message || 'Failed to generate summary.');
    } finally { setSummaryLoading(false); }
  };

  const handleExport = async () => {
    if (!selectedEventId || !exportStart || !exportEnd) {
      setExportError('Please select an event and date range.');
      return;
    }
    setExportError('');
    setExportLoading(true);
    try {
      const selectedEvent = events.find(e => e._id === selectedEventId);
      if (!selectedEvent) { setExportError('Event not found.'); return; }
      const res = await sessionsApi.exportConversations({
        eventId: (selectedEvent as any).eventId || selectedEventId,
        startDate: exportStart,
        endDate: exportEnd,
        format: exportFormat,
      });
      const { csvData, jsonData, exportInfo } = (res.data as any)?.data || {};
      if (csvData && (exportFormat === 'csv' || exportFormat === 'both')) {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(new Blob([csvData], { type: 'text/csv' }));
        link.download = `conversations_${exportStart}_${exportEnd}.csv`;
        link.click(); URL.revokeObjectURL(link.href);
      }
      if (jsonData && (exportFormat === 'json' || exportFormat === 'both')) {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' }));
        link.download = `conversations_${exportStart}_${exportEnd}.json`;
        link.click(); URL.revokeObjectURL(link.href);
      }
      const info = exportInfo;
      if (info) alert(`Exported ${info.totalConversations ?? '?'} conversations from ${info.totalSessions ?? '?'} sessions.`);
      setExportOpen(false);
    } catch (err: any) {
      setExportError(err.response?.data?.message || 'Export failed.');
    } finally { setExportLoading(false); }
  };

  // ── Effects ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    const timer = setTimeout(() => setNameSearch(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (isAdmin) fetchClients();
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) { fetchEvents(selectedClientId || undefined); setSelectedEventId(''); setSessions([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId]);

  useEffect(() => {
    if (selectedEventId) { setSessionsPage(1); fetchSessions(selectedEventId, 1); }
    else { setSessions([]); }
    setSelectedSession(null);
    setConversations([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEventId]);

  useEffect(() => {
    if (selectedEventId) fetchSessions(selectedEventId, sessionsPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionsPage]);

  useEffect(() => {
    if (!selectedEventId) return;
    setSessionsPage(1);
    fetchSessions(selectedEventId, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameSearch]);

  useEffect(() => {
    if (selectedSession) fetchHistory(selectedSession.sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSession]);

  const totalPages = Math.max(1, Math.ceil(sessionsTotal / PAGE_SIZE));

  function handleSessionSelect(session: Session) {
    setSelectedSession(session);
    setActiveView('chat');
    setSummary('');
    setSummaryError('');
  }

  function handleReset() {
    const nextStart = defaultStartDateTime();
    const nextEnd = defaultEndDateTime();
    setSearchInput('');
    setNameSearch('');
    setStartDate(nextStart);
    setEndDate(nextEnd);
    setSessionsPage(1);
    if (selectedEventId) fetchSessions(selectedEventId, 1, { search: '', startDate: nextStart, endDate: nextEnd });
  }

  function handleApplyFilters() {
    if (!selectedEventId) return;
    setNameSearch(searchInput.trim());
    setSessionsPage(1);
    fetchSessions(selectedEventId, 1, { search: searchInput.trim() });
  }

  function handleEventChange(eventId: string) {
    setSelectedEventId(eventId);
    setSessionsPage(1);
    setSelectedSession(null);
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  const selectedEventName = events.find(e => e._id === selectedEventId)?.name ?? '';

  return (
    <div className="flex flex-col -m-6 bg-gray-50" style={{ height: 'calc(100vh - 64px)' }}>

      {/* ── Page Header ───────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Conversations</h1>
            <p className="text-sm text-gray-500 mt-0.5">Browse and replay sessions for each Knowledge Assistant</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {isAdmin && (
              <select
                value={selectedClientId}
                onChange={e => setSelectedClientId(e.target.value)}
                disabled={loadingClients}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[180px]"
              >
                <option value="">All Clients</option>
                {clients.map(c => (
                  <option key={c._id} value={c._id}>{c.name || c.email}</option>
                ))}
              </select>
            )}
            <select
              value={selectedEventId}
              onChange={e => handleEventChange(e.target.value)}
              disabled={eventsLoading}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
            >
              <option value="">Select Knowledge Assistant</option>
              {events.map(e => (
                <option key={e._id} value={e._id}>{e.name}</option>
              ))}
            </select>
            <button
              onClick={() => setExportOpen(true)}
              disabled={!selectedEventId}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* ── Body ──────────────────────────────────────────────────────────────── */}
      <div className="flex flex-1 min-h-0">

        {/* ── LEFT: Session List ─────────────────────────────────────────────── */}
        <div className="w-80 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col">

          {/* Search & date filters */}
          <div className="px-4 py-3 border-b border-gray-100 space-y-2 flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search users by name…"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="space-y-1.5">
              <input type="datetime-local" value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex items-center gap-1.5">
                <input type="datetime-local" value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  className="flex-1 px-2 py-1.5 border border-gray-200 rounded-lg text-xs bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button onClick={handleApplyFilters}
                  className="px-2.5 py-1.5 rounded-lg text-[11px] font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition-colors">
                  Apply
                </button>
                <button onClick={handleReset} title="Reset filters"
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>

          {/* Count */}
          <div className="px-4 py-2 border-b border-gray-100 flex-shrink-0">
            <p className="text-xs text-gray-400 font-medium">
              {sessionsLoading ? 'Loading…' : `${sessionsTotal} session${sessionsTotal !== 1 ? 's' : ''}`}
            </p>
          </div>

          {/* Session items */}
          <div className="flex-1 overflow-y-auto">
            {!selectedEventId ? (
              <div className="flex flex-col items-center justify-center h-full py-16 text-center px-4">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                  <MessageSquare className="w-5 h-5 text-gray-300" />
                </div>
                <p className="text-sm text-gray-500 font-medium">Select a Knowledge Assistant</p>
              </div>
            ) : sessionsLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : sessions.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-16 text-center px-4">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                  <MessageSquare className="w-5 h-5 text-gray-300" />
                </div>
                <p className="text-sm text-gray-500 font-medium">No sessions found</p>
                <p className="text-xs text-gray-400 mt-1">Try adjusting the filters</p>
              </div>
            ) : sessions.map(session => {
              const isActive = selectedSession?.sessionId === session.sessionId;
              const avatarSeed = session.userData?.name || session.userId;
              const displayTitle = session.title || session.userData?.name || session.userId;
              const displaySubtitle = session.title ? (session.userData?.name || null) : null;
              const color = avatarColor(avatarSeed);
              return (
                <button key={session.sessionId}
                  onClick={() => handleSessionSelect(session)}
                  className={`w-full text-left px-4 py-3.5 border-b border-gray-100 transition-all ${
                    isActive ? 'bg-blue-50 border-l-2 border-l-blue-500' : 'hover:bg-gray-50 border-l-2 border-l-transparent'
                  }`}
                >
                  <div className="flex items-start gap-2.5">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${color}`}>
                      {getInitials(avatarSeed)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-1">
                        <p className={`text-sm font-medium truncate ${isActive ? 'text-blue-700' : 'text-gray-900'}`}>{displayTitle}</p>
                        <span className="text-[10px] text-gray-400 whitespace-nowrap flex-shrink-0">
                          {dayjs(session.createdAt).format('MMM D')}
                        </span>
                      </div>
                      {displaySubtitle && (
                        <p className="text-[11px] text-gray-400 truncate">{displaySubtitle}</p>
                      )}
                      <div className="flex items-center gap-3 mt-1">
                        {!session.isActive && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">Inactive</span>
                        )}
                        <span className="flex items-center gap-1 text-[10px] text-gray-400">
                          <Clock className="w-2.5 h-2.5" />
                          {dayjs(session.updatedAt || session.createdAt).format('HH:mm')}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
              <button onClick={() => setSessionsPage(p => Math.max(1, p - 1))}
                disabled={sessionsPage === 1}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-gray-500">{sessionsPage} / {totalPages}</span>
              <button onClick={() => setSessionsPage(p => Math.min(totalPages, p + 1))}
                disabled={sessionsPage === totalPages}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* ── RIGHT: Conversation Panel ──────────────────────────────────────── */}
        <div className="flex-1 min-w-0 flex flex-col">
          {selectedSession ? (
            <>
              {/* Panel header */}
              <div className="bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between gap-4 flex-wrap flex-shrink-0">
                {/* User info */}
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${avatarColor(selectedSession.userData?.name || selectedSession.userId)}`}>
                    {getInitials(selectedSession.userData?.name || selectedSession.userId)}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {selectedSession.userData?.name || selectedSession.userId}
                    </p>
                    {selectedSession.userData?.email && (
                      <p className="text-xs text-gray-500">{selectedSession.userData.email}</p>
                    )}
                  </div>
                </div>

                {/* View toggle */}
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                  {([
                    { key: 'chat' as const, icon: MessageSquare, label: 'Chat' },
                    { key: 'summary' as const, icon: FileText, label: 'Summary' },
                  ]).map(({ key, icon: Icon, label }) => (
                    <button key={key} onClick={() => setActiveView(key)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap ${
                        activeView === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                      }`}>
                      <Icon className="w-3.5 h-3.5" />
                      {label}
                    </button>
                  ))}
                </div>

                {/* Session meta */}
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1.5 text-xs text-gray-500">
                    <Calendar className="w-3.5 h-3.5 text-gray-400" />
                    {dayjs(selectedSession.createdAt).format('MMM D, YYYY · HH:mm')}
                  </div>
                  {selectedEventName && (
                    <span className="px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-xs font-medium">
                      {selectedEventName}
                    </span>
                  )}
                </div>
              </div>

              {/* ── Chat view ── */}
              {activeView === 'chat' && (
                <>
                  <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                    {historyLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        <span className="ml-3 text-sm text-gray-500">Loading conversation…</span>
                      </div>
                    ) : conversations.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-16">
                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                          <MessageSquare className="w-6 h-6 text-gray-300" />
                        </div>
                        <p className="text-gray-500 text-sm">No messages found</p>
                      </div>
                    ) : conversations.map((conv, idx) => (
                      <div key={conv.messageId || idx} className="space-y-3">
                        {/* User message */}
                        <div className="flex gap-3 justify-end">
                          <div className="max-w-[72%] flex flex-col gap-1 items-end">
                            <div className="flex items-center gap-2 flex-row-reverse">
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">User</span>
                              <span className="text-[10px] text-gray-400 font-mono">
                                {dayjs(conv.timestamp).format('HH:mm')}
                              </span>
                            </div>
                            <div className="px-4 py-3 rounded-2xl rounded-tr-sm text-sm leading-relaxed bg-blue-600 text-white">
                              {conv.userMessage}
                            </div>
                          </div>
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-semibold ${avatarColor(selectedSession.userData?.name || selectedSession.userId)}`}>
                            {getInitials(selectedSession.userData?.name || selectedSession.userId)}
                          </div>
                        </div>
                        {/* Bot response */}
                        <div className="flex gap-3 justify-start">
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm">
                            <Bot className="w-3.5 h-3.5 text-white" />
                          </div>
                          <div className="max-w-[72%] flex flex-col gap-1 items-start">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Assistant</span>
                              <span className="text-[10px] text-gray-400 font-mono">
                                {dayjs(conv.timestamp).format('HH:mm')}
                              </span>
                            </div>
                            <div className="px-4 py-3 rounded-2xl rounded-tl-sm text-sm leading-relaxed bg-white text-gray-800 border border-gray-200 shadow-sm prose prose-sm max-w-none">
                              <ReactMarkdown remarkPlugins={[remarkGfm]} children={conv.botResponse} />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="bg-white border-t border-gray-100 px-6 py-3 flex items-center gap-2 flex-shrink-0">
                    <Clock className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-xs text-gray-400">
                      Session ended · Last activity at{' '}
                      <span className="font-medium text-gray-500">
                        {dayjs(selectedSession.updatedAt || selectedSession.createdAt).format('MMM D, HH:mm')}
                      </span>
                    </span>
                  </div>
                </>
              )}

              {/* ── Summary view ── */}
              {activeView === 'summary' && (
                <div className="flex-1 overflow-y-auto px-6 py-5">
                  {!summary && !summaryLoading && !summaryError && (
                    <div className="flex flex-col items-center justify-center py-16">
                      <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mb-4">
                        <FileText className="w-8 h-8 text-blue-400" />
                      </div>
                      <p className="text-base font-semibold text-gray-700 mb-1">Generate a summary</p>
                      <p className="text-sm text-gray-400 max-w-xs text-center mb-5">
                        Use AI to generate a concise summary of this conversation.
                      </p>
                        <button
                          onClick={() => generateSummary(false)}
                          disabled={conversations.length === 0}
                          className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                        Generate Summary
                      </button>
                    </div>
                  )}
                  {summaryLoading && (
                    <div className="flex items-center justify-center py-12">
                      <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                      <span className="ml-3 text-sm text-gray-500">Generating summary…</span>
                    </div>
                  )}
                  {summaryError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-800 mb-4">
                      {summaryError}
                    </div>
                  )}
                  {summary && !summaryLoading && (
                    <div className="space-y-4">
                      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                          <FileText className="w-4 h-4 text-gray-400" />
                          <h3 className="text-sm font-semibold text-gray-900">AI-Generated Summary</h3>
                        </div>
                        <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed">
                          <ReactMarkdown remarkPlugins={[remarkGfm]} children={summary} />
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <button
                          onClick={() => generateSummary(true)}
                          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <RefreshCw className="w-4 h-4" />
                          Regenerate
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center mb-4 shadow-sm">
                <MessageSquare className="w-8 h-8 text-blue-400" />
              </div>
              <p className="text-base font-semibold text-gray-700 mb-1">Select a session</p>
              <p className="text-sm text-gray-400 max-w-xs">
                Choose a session from the list on the left to replay the full conversation.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Export Modal ──────────────────────────────────────────────────────── */}
      {exportOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setExportOpen(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Export Conversations</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                <div className="flex items-center gap-2">
                  <input type="date" value={exportStart} onChange={e => setExportStart(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-gray-400">→</span>
                  <input type="date" value={exportEnd} onChange={e => setExportEnd(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Format</label>
                <div className="flex gap-3">
                  {(['csv', 'json', 'both'] as const).map(fmt => (
                    <label key={fmt} className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="exportFormat" value={fmt} checked={exportFormat === fmt}
                        onChange={() => setExportFormat(fmt)} className="accent-blue-600" />
                      <span className="text-sm text-gray-700 uppercase">{fmt}</span>
                    </label>
                  ))}
                </div>
              </div>
              {exportError && (
                <p className="text-sm text-red-600">{exportError}</p>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setExportOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button onClick={handleExport} disabled={exportLoading || !exportStart || !exportEnd}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center gap-2">
                {exportLoading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                Export
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Conversations;
